import type { Grinder, ChartConfig, BrewingMethod } from '@/types/grinder';
import { micronToPosition } from '@/lib/data';

/**
 * Default chart configuration
 */
const DEFAULT_CONFIG: ChartConfig = {
  width: 960,
  height: 350,
  marginTop: 50,
  marginBottom: 37,
  marginLeft: 30,
  marginRight: 30,
  chartHeight: 263,
  topAxisY: 0,
  bottomAxisY: 313
};

/**
 * Fixed micron range for all charts (for consistent comparison)
 */
const FIXED_MICRON_MIN = 0;
const FIXED_MICRON_MAX = 1400;

/**
 * Create an SVG element with namespace
 */
function createSVGElement(tag: string, attributes: Record<string, string | number> = {}): SVGElement {
  const element = document.createElementNS('http://www.w3.org/2000/svg', tag);
  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, String(value));
  });
  return element;
}

/**
 * Create text element with content
 */
function createText(
  x: number,
  y: number,
  content: string,
  attributes: Record<string, string | number> = {}
): SVGTextElement {
  const text = createSVGElement('text', {
    x,
    y,
    ...attributes
  }) as SVGTextElement;
  text.textContent = content;
  return text;
}

/**
 * Generate the complete SVG chart for a grinder
 */
export function generateGrinderChart(grinder: Grinder, brewingMethods?: BrewingMethod[], config: ChartConfig = DEFAULT_CONFIG): SVGSVGElement {
  const svg = createSVGElement('svg', {
    viewBox: `0 0 ${config.width} ${config.height}`,
    width: '100%',
    height: 'auto'
  }) as SVGSVGElement;

  // Add title
  const title = createSVGElement('title');
  title.textContent = `${grinder.brand} ${grinder.model} grind size chart`;
  svg.appendChild(title);

  // Add defs for patterns and reusable elements
  const defs = createSVGElement('defs');
  svg.appendChild(defs);

  // Calculate chart dimensions
  const chartWidth = config.width - config.marginLeft - config.marginRight;

  // Use fixed micron range for all charts (for consistent comparison)
  const minMicron = FIXED_MICRON_MIN;
  const maxMicron = FIXED_MICRON_MAX;

  // Get the grinder's actual micron range
  const grinderMinMicron = grinder.chart_settings.micronRange.min;
  const grinderMaxMicron = grinder.chart_settings.micronRange.max;

  // Draw striped areas FIRST (before grid lines) so grid lines appear on top
  // Draw striped area for out-of-range region at the beginning (if grinder doesn't start at 0)
  if (grinderMinMicron > minMicron) {
    const outOfRangeStartX = config.marginLeft;
    const outOfRangeEndX = config.marginLeft + micronToPosition(grinderMinMicron, minMicron, maxMicron, chartWidth);
    const outOfRangeWidth = outOfRangeEndX - outOfRangeStartX;

    svg.appendChild(createSVGElement('rect', {
      fill: 'url(#stripes)',
      height: config.chartHeight,
      width: outOfRangeWidth,
      x: outOfRangeStartX,
      y: config.marginTop + 0.75,
      stroke: 'none',
      'stroke-width': 0
    }));
  }

  // Draw striped area for out-of-range region at the end (if grinder doesn't cover full range)
  if (grinderMaxMicron < maxMicron) {
    const outOfRangeStartX = config.marginLeft + micronToPosition(grinderMaxMicron, minMicron, maxMicron, chartWidth);
    const outOfRangeWidth = (config.width - config.marginRight) - outOfRangeStartX;

    svg.appendChild(createSVGElement('rect', {
      fill: 'url(#stripes)',
      height: config.chartHeight,
      width: outOfRangeWidth,
      x: outOfRangeStartX,
      y: config.marginTop + 0.75,
      stroke: 'none',
      'stroke-width': 0
    }));
  }

  // Create main group for grid lines (rendered AFTER striped areas so they appear on top)
  const gridGroup = createSVGElement('g', {
    transform: `translate(0 ${config.marginTop})`
  });
  svg.appendChild(gridGroup);

  // Render grid lines
  renderGridLines(gridGroup, defs, grinder, minMicron, maxMicron, chartWidth, config);

  // Draw left border at 0 microns
  svg.appendChild(createSVGElement('rect', {
    stroke: 'var(--text)',
    x: config.marginLeft,
    y: config.marginTop + 0.75,
    height: config.chartHeight,
    'stroke-width': 0,
    width: 1.5,
    fill: 'var(--text)'
  }));

  // Draw right border at 1400 microns
  svg.appendChild(createSVGElement('rect', {
    stroke: 'var(--text)',
    x: config.width - config.marginRight,
    y: config.marginTop + 0.75,
    height: config.chartHeight,
    'stroke-width': 0,
    width: 1.5,
    fill: 'var(--text)'
  }));

  // Render units label at top (brand/model name is displayed outside SVG)
  if (grinder.units) {
    const unitsGroup = createSVGElement('g', {
      transform: `translate(${config.marginLeft} 5)`
    });
    svg.appendChild(unitsGroup);

    unitsGroup.appendChild(createText(0, 8, grinder.units, {
      'dominant-baseline': 'middle',
      'font-size': 12,
      fill: 'var(--text)',
      'font-family': 'sans'
    }));
  }

  // Render top axis (grinder ticks)
  const topAxisGroup = createSVGElement('g', {
    transform: `translate(0 ${config.marginTop})`
  });
  svg.appendChild(topAxisGroup);
  renderTopAxis(topAxisGroup, defs, grinder, minMicron, maxMicron, chartWidth, config);

  // Render brewing method boxes
  if (brewingMethods && brewingMethods.length > 0) {
    const brewingBoxesGroup = createSVGElement('g', {
      transform: `translate(0 ${config.marginTop})`
    });
    svg.appendChild(brewingBoxesGroup);
    renderBrewingMethodBoxes(brewingBoxesGroup, brewingMethods, minMicron, maxMicron, chartWidth);
  }

  // Render bottom axis (micron scale)
  const bottomAxisGroup = createSVGElement('g', {
    transform: `translate(0 ${config.bottomAxisY})`
  });
  svg.appendChild(bottomAxisGroup);
  renderBottomAxis(bottomAxisGroup, minMicron, maxMicron, chartWidth, config);

  // Add stripes pattern to defs
  const stripesPattern = createSVGElement('pattern', {
    id: 'stripes',
    viewBox: '0 0 8 8',
    width: 8,
    height: 8,
    patternUnits: 'userSpaceOnUse',
    patternTransform: 'rotate(-45)'
  });
  defs.appendChild(stripesPattern);

  const stripesPath = createSVGElement('path', {
    d: 'M-1 2h20v1h-20z',
    fill: 'var(--lines)'
  });
  stripesPattern.appendChild(stripesPath);

  return svg;
}

/**
 * Render vertical grid lines
 */
function renderGridLines(
  group: SVGElement,
  defs: SVGElement,
  grinder: Grinder,
  minMicron: number,
  maxMicron: number,
  chartWidth: number,
  config: ChartConfig
): void {
  const grinderMinMicron = grinder.chart_settings.micronRange.min;
  const grinderMaxMicron = grinder.chart_settings.micronRange.max;
  const { mainTicks } = grinder.chart_settings;

  // Create grid line template in defs (positioned at origin for reuse)
  const gridLine = createSVGElement('rect', {
    height: config.chartHeight,
    width: 1,
    fill: 'var(--lines)',
    x: 0,
    id: 'g'
  });
  defs.appendChild(gridLine);

  // Calculate positions for main ticks using grinder's actual range
  const tickCount = grinder.chart_settings.tickValues.length;

  mainTicks.forEach(tickIndex => {
    // Calculate micron value for this tick based on grinder's range
    const micron = grinderMinMicron + ((tickIndex / (tickCount - 1)) * (grinderMaxMicron - grinderMinMicron));

    // Only draw grid lines within the fixed 0-1400 micron range
    if (micron < minMicron || micron > maxMicron) return;

    // Position it on the fixed 0-1400 scale
    const x = config.marginLeft + micronToPosition(micron, minMicron, maxMicron, chartWidth);

    const use = createSVGElement('use', {
      href: '#g',
      x: x
    });
    group.appendChild(use);
  });
}

/**
 * Render top axis with grinder tick marks and labels
 */
function renderTopAxis(
  group: SVGElement,
  defs: SVGElement,
  grinder: Grinder,
  minMicron: number,
  maxMicron: number,
  chartWidth: number,
  config: ChartConfig
): void {
  const grinderMinMicron = grinder.chart_settings.micronRange.min;
  const grinderMaxMicron = grinder.chart_settings.micronRange.max;
  const { tickValues, mainTicks, visibleLabels } = grinder.chart_settings;
  const tickCount = tickValues.length;

  // Create tick mark templates in defs (positioned at origin for reuse)
  const mainTickMark = createSVGElement('rect', {
    y: -10,
    height: 10,
    width: 1.5,
    fill: 'var(--text)',
    x: 0,
    id: 'top-main'
  });
  defs.appendChild(mainTickMark);

  const subTickMark = createSVGElement('rect', {
    y: -5,
    height: 5,
    width: 1,
    fill: 'var(--text)',
    x: 0,
    id: 'top-sub'
  });
  defs.appendChild(subTickMark);

  // Render all tick marks (including tick 0)
  tickValues.forEach((_, index) => {
    // Calculate micron value for this tick based on grinder's range
    const micron = grinderMinMicron + ((index / (tickCount - 1)) * (grinderMaxMicron - grinderMinMicron));

    // Position it on the fixed 0-1400 scale
    const x = config.marginLeft + micronToPosition(micron, minMicron, maxMicron, chartWidth);
    const isMainTick = mainTicks.includes(index);

    const use = createSVGElement('use', {
      href: isMainTick ? '#top-main' : '#top-sub',
      x: x
    });
    group.appendChild(use);
  });

  // Draw top border - always extend across full 0-1400 micron range
  group.appendChild(createSVGElement('rect', {
    x: config.marginLeft,
    y: 0,
    width: chartWidth + 1.5,
    height: 1.5,
    fill: 'var(--text)'
  }));

  // Render tick labels (scaled down for smaller chart)
  visibleLabels.forEach(tickIndex => {
    const micron = grinderMinMicron + ((tickIndex / (tickCount - 1)) * (grinderMaxMicron - grinderMinMicron));
    const x = config.marginLeft + micronToPosition(micron, minMicron, maxMicron, chartWidth) + 0.75;

    group.appendChild(createText(x, -18, tickValues[tickIndex] || '', {
      'text-anchor': 'middle',
      'dominant-baseline': 'middle',
      'font-size': 13,
      'font-family': 'code',
      'font-weight': 400,
      fill: 'var(--text)'
    }));
  });
}


/**
 * Render brewing method boxes inside the chart
 */
function renderBrewingMethodBoxes(
  group: SVGElement,
  brewingMethods: Array<{ label: string; min: number; max: number; level: number }>,
  minMicron: number,
  maxMicron: number,
  chartWidth: number
): void {
  // Use level property to calculate Y position
  // Base Y position starts at 15, then add 30 pixels per level (scaled down 75%)
  const baseY = 15;
  const levelSpacing = 30;
  const boxHeight = 22;

  brewingMethods.forEach((method) => {
    const startX = 30 + micronToPosition(method.min, minMicron, maxMicron, chartWidth);
    const endX = 30 + micronToPosition(method.max, minMicron, maxMicron, chartWidth);
    const width = endX - startX;
    const y = baseY + (method.level * levelSpacing);

    group.appendChild(createSVGElement('rect', {
      x: startX,
      y,
      width,
      height: boxHeight,
      fill: 'var(--glare)',
      'stroke-width': 1.5,
      stroke: 'var(--text)'
    }));

    group.appendChild(createText(startX + width / 2, y + (boxHeight / 2) + 6, method.label, {
      'text-anchor': 'middle',
      'font-size': 14,
      fill: 'var(--text)'
    }));
  });
}

/**
 * Render bottom axis with micron scale (always 0-1400)
 */
function renderBottomAxis(
  group: SVGElement,
  minMicron: number,
  maxMicron: number,
  chartWidth: number,
  config: ChartConfig
): void {
  // Always show 0, 200, 400, 600, 800, 1000, 1200, 1400
  const micronValues = [0, 200, 400, 600, 800, 1000, 1200, 1400];

  // Create tick mark template
  const tickMark = createSVGElement('rect', {
    y: 0,
    height: 26,
    width: 1.5,
    fill: 'var(--text)',
    x: config.marginLeft,
    id: 'bottom-tick'
  });
  group.appendChild(tickMark);

  // Render tick marks
  micronValues.forEach(micron => {
    const x = config.marginLeft + micronToPosition(micron, minMicron, maxMicron, chartWidth);
    const use = createSVGElement('use', {
      href: '#bottom-tick',
      x: x - config.marginLeft
    });
    group.appendChild(use);
  });

  // Draw bottom border
  group.appendChild(createSVGElement('rect', {
    x: config.marginLeft,
    y: 0,
    width: chartWidth + 1.5,
    height: 1.5,
    fill: 'var(--text)'
  }));

  // Render micron labels (scaled down for smaller chart)
  micronValues.forEach((micron, index) => {
    const x = config.marginLeft + micronToPosition(micron, minMicron, maxMicron, chartWidth) + 0.75;
    const label = index === micronValues.length - 1 ? `${micron}μm` : String(micron);

    group.appendChild(createText(x, 32, label, {
      'text-anchor': 'middle',
      'dominant-baseline': 'middle',
      'font-size': 13,
      'font-family': 'code',
      'font-weight': 350,
      fill: 'var(--text)'
    }));
  });

  // Render grind size categories (always show all)
  const categories = [
    { label: 'Extra Fine', start: 0, end: 200 },
    { label: 'Fine', start: 200, end: 400 },
    { label: 'Medium Fine', start: 400, end: 600 },
    { label: 'Medium', start: 600, end: 800 },
    { label: 'Medium Coarse', start: 800, end: 1000 },
    { label: 'Coarse', start: 1000, end: 1200 },
    { label: 'Extra Coarse', start: 1200, end: 1400 }
  ];

  categories.forEach(category => {
    const x = config.marginLeft + micronToPosition((category.start + category.end) / 2, minMicron, maxMicron, chartWidth);

    group.appendChild(createText(x, 12, category.label, {
      'text-anchor': 'middle',
      'dominant-baseline': 'middle',
      'font-size': 13,
      'font-family': 'sans',
      'font-weight': 350,
      fill: 'var(--text)'
    }));
  });
}

/**
 * Create a comparison line that spans across both charts
 */
export function createComparisonLine(x: number, height: number): SVGLineElement {
  return createSVGElement('line', {
    x1: x,
    y1: 0,
    x2: x,
    y2: height,
    stroke: 'var(--accent)',
    'stroke-width': 2,
    'stroke-dasharray': '5,5',
    opacity: 0.7,
    'pointer-events': 'none'
  }) as SVGLineElement;
}
